import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';

import { type TemplatePickerOption } from '~/components/cara-input/cara-input-template-picker-dialog';
import {
  EMPTY_ANSWERS,
  MAX_TASKS,
  suggestedUseCases,
  type BookAnswer,
  type SuggestionAnswers
} from './preset-suggestions';
import { type HomePin } from '~/api/data/home/use-pinned-home-items';
import { UseCaseInfoDialog } from './use-case-info-dialog';
import { UseCaseCard } from './use-case-tile';
import {
  type RunCardTemplateKind,
  type UseCaseSpec,
  type WorkMoment,
  type WorkloadTask
} from './use-case-catalog';
import { Fieldset, Legend } from '@oysterjs/ui/fieldset';

type Choice<T> = { value: T; label: string; hint?: string };

const BOOK_CHOICES: Array<Choice<BookAnswer>> = [
  { value: 'personal', label: 'Personal lines', hint: 'Auto, home, umbrella' },
  { value: 'commercial', label: 'Commercial lines', hint: 'Property, liability, workers comp' },
  { value: 'benefits', label: 'Employee benefits', hint: 'Medical, dental, vision, life' },
  { value: 'mixed', label: 'More than one of these', hint: 'Suggest across all of them' }
];

const TASK_CHOICES: Array<Choice<WorkloadTask>> = [
  { value: 'checkDocuments', label: 'Checking documents for mistakes or missing information' },
  { value: 'fillForms', label: 'Filling applications, certificates, ID cards and forms' },
  { value: 'clientDocuments', label: 'Putting proposals and summaries together for clients' },
  { value: 'findMarket', label: 'Shopping a risk and finding a market' },
  { value: 'answerCoverage', label: 'Answering “is this covered?”' },
  { value: 'spreadsheets', label: 'Working in spreadsheets' },
  { value: 'claims', label: 'Working claims' }
];

const MOMENT_CHOICES: Array<Choice<WorkMoment>> = [
  { value: 'newBusiness', label: 'Before new business goes to market' },
  { value: 'renewal', label: 'At renewal, or when remarketing' },
  { value: 'service', label: 'Everyday account service' },
  { value: 'claims', label: 'Once a claim is open' },
  { value: 'clientMeetings', label: 'Getting ready for client meetings' }
];

const STEPS = ['book', 'tasks', 'moments', 'results'] as const;
type Step = (typeof STEPS)[number];

const ChoiceRow = <T,>({
  choice,
  selected,
  disabled,
  onSelect
}: {
  choice: Choice<T>;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) => (
  <Headless.Button
    type="button"
    disabled={disabled}
    onClick={onSelect}
    aria-pressed={selected}
    className={clsx(
      'flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-colors',
      selected
        ? 'border-primary-300 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10'
        : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5',
      'disabled:pointer-events-none disabled:opacity-40'
    )}
  >
    <span
      className={clsx(
        'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
        selected
          ? 'border-primary-500 bg-primary-500 text-white'
          : 'border-neutral-300 dark:border-neutral-600'
      )}
    >
      {selected && <CheckIcon className="size-3.5" />}
    </span>
    <span className="min-w-0">
      <span className="block text-sm font-medium text-neutral-900 dark:text-white">
        {choice.label}
      </span>
      {choice.hint && (
        <span className="block text-xs text-neutral-500 dark:text-neutral-400">{choice.hint}</span>
      )}
    </span>
  </Headless.Button>
);

const StepProgress = ({ index }: { index: number }) => (
  <div className="flex gap-1.5" aria-hidden="true">
    {STEPS.slice(0, 3).map((step, i) => (
      <motion.span
        key={step}
        className="h-1 w-8 rounded-full bg-neutral-200 dark:bg-neutral-700"
        animate={{ backgroundColor: i <= index ? 'rgb(14 165 233)' : 'rgb(229 231 235)' }}
        transition={{ duration: 0.25 }}
        aria-hidden="true"
      />
    ))}
  </div>
);

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/** Mount inside an `AnimatePresence`. */
export const PresetSuggestionFlow = (props: {
  onClose: () => void;
  isPinned: (pin: HomePin) => boolean;
  onTogglePin: (pin: HomePin) => void;
  onPinAll: (pins: HomePin[]) => void;
  onBrowseAll: () => void;
  libraries: Partial<Record<RunCardTemplateKind, TemplatePickerOption[]>>;
  hasProAccess: boolean;
}) => {
  const stillness = useReducedMotion();
  const [step, setStep] = React.useState<Step>('book');
  const [answers, setAnswers] = React.useState<SuggestionAnswers>(EMPTY_ANSWERS);
  const [explaining, setExplaining] = React.useState<UseCaseSpec>();

  const index = STEPS.indexOf(step);

  // Held from the last step so pinning a tile cannot reshuffle the six.
  const [results, setResults] = React.useState<UseCaseSpec[]>([]);

  const canAdvance =
    (step === 'book' && !!answers.book) ||
    (step === 'tasks' && answers.tasks.length > 0) ||
    (step === 'moments' && answers.moments.length > 0);

  const next = () => {
    if (step === 'moments') {
      setResults(
        suggestedUseCases(answers, {
          libraries: props.libraries,
          hasProAccess: props.hasProAccess
        })
      );
    }
    setStep(STEPS[Math.min(index + 1, STEPS.length - 1)]);
  };

  const unpinned = results.filter(
    (useCase) => !props.isPinned({ kind: 'useCase', id: useCase.id })
  );

  return (
    <>
      <MountableDialog onClose={props.onClose} size="3xl">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <DialogTitle className="m-0 text-wrap">
              {step === 'results' ? 'Presets for how you work' : 'Let’s set up your home page'}
            </DialogTitle>
          </div>
          {/* Mirrors the slideout's close affordance. A flow someone opened out of
              curiosity needs a way out that is not the end of it. */}
          <button
            type="button"
            onClick={props.onClose}
            className="relative -mt-1 rounded-md bg-white text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-neutral-900 dark:text-neutral-500 dark:hover:text-neutral-400"
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close</span>
            <XMarkIcon aria-hidden="true" className="size-5" />
          </button>
        </div>
        <DialogDescription>
          {step === 'results'
            ? 'Pin the ones you want. They land on your home page in the order you pin them, and you can change what is pinned any time.'
            : 'Three quick questions, then we’ll suggest what to pin.'}
        </DialogDescription>

        <DialogBody>
          {step !== 'results' && (
            <div className="mb-5">
              <StepProgress index={index} />
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{ opacity: 0, x: stillness ? 0 : 16 }}
              animate={{ opacity: 1, x: 0 }}
              // Faster exit: `mode="wait"` runs them back to back.
              exit={{ opacity: 0, x: stillness ? 0 : -16, transition: { duration: 0.12 } }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {step === 'book' && (
                <Fieldset>
                  <Legend>Which book do you write?</Legend>
                  <p className="m-0 mb-3 text-sm text-neutral-500 dark:text-neutral-400">
                    Pick one.
                  </p>
                  <div className="flex flex-col gap-2">
                    {BOOK_CHOICES.map((choice) => (
                      <ChoiceRow
                        key={choice.value}
                        choice={choice}
                        selected={answers.book === choice.value}
                        disabled={false}
                        onSelect={() => setAnswers((prev) => ({ ...prev, book: choice.value }))}
                      />
                    ))}
                  </div>
                </Fieldset>
              )}

              {step === 'tasks' && (
                <Fieldset>
                  <Legend>In a normal week, what takes the most time?</Legend>
                  <p className="m-0 mb-3 text-sm text-neutral-500 dark:text-neutral-400">
                    Pick up to {MAX_TASKS}.
                  </p>
                  <div className="flex flex-col gap-2">
                    {TASK_CHOICES.map((choice) => {
                      const selected = answers.tasks.includes(choice.value);
                      return (
                        <ChoiceRow
                          key={choice.value}
                          choice={choice}
                          selected={selected}
                          disabled={!selected && answers.tasks.length >= MAX_TASKS}
                          onSelect={() =>
                            setAnswers((prev) => ({
                              ...prev,
                              tasks: toggle(prev.tasks, choice.value)
                            }))
                          }
                        />
                      );
                    })}
                  </div>
                </Fieldset>
              )}

              {step === 'moments' && (
                <Fieldset>
                  <Legend>When does that work pile up?</Legend>
                  <p className="m-0 mb-3 text-sm text-neutral-500 dark:text-neutral-400">
                    Pick as many as apply.
                  </p>
                  <div className="flex flex-col gap-2">
                    {MOMENT_CHOICES.map((choice) => (
                      <ChoiceRow
                        key={choice.value}
                        choice={choice}
                        selected={answers.moments.includes(choice.value)}
                        disabled={false}
                        onSelect={() =>
                          setAnswers((prev) => ({
                            ...prev,
                            moments: toggle(prev.moments, choice.value)
                          }))
                        }
                      />
                    ))}
                  </div>
                </Fieldset>
              )}

              {step === 'results' && (
                <div role="group" aria-label="Suggested presets">
                  {results.length === 0 ? (
                    <p className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
                      Nothing in the catalog lines up with those answers yet. The browser has
                      everything, if you want a look.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {results.map((useCase, i) => (
                        <motion.div
                          key={useCase.id}
                          initial={{ opacity: 0, y: stillness ? 0 : 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.2,
                            delay: stillness ? 0 : i * 0.05,
                            ease: 'easeOut'
                          }}
                        >
                          <UseCaseCard
                            useCase={useCase}
                            pinned={props.isPinned({ kind: 'useCase', id: useCase.id })}
                            onTogglePin={() =>
                              props.onTogglePin({ kind: 'useCase', id: useCase.id })
                            }
                            onExplain={() => setExplaining(useCase)}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Six is a starting point. The catalog is one click away for
                      anyone whose work these six missed. */}
                  <div className="mt-4 flex justify-center">
                    <Button plain onClick={props.onBrowseAll} className="text-sm">
                      Browse all presets
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </DialogBody>

        <DialogActions>
          {step === 'results' ? (
            <>
              <Button color="white" onClick={() => setStep('moments')}>
                Back
              </Button>
              {unpinned.length > 0 && (
                <Button
                  color="white"
                  onClick={() =>
                    props.onPinAll(unpinned.map((useCase) => ({ kind: 'useCase', id: useCase.id })))
                  }
                >
                  Pin all {unpinned.length}
                </Button>
              )}
              <Button color="sky" onClick={props.onClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button
                color="white"
                disabled={index === 0}
                onClick={() => setStep(STEPS[Math.max(index - 1, 0)])}
              >
                Back
              </Button>
              <Button color="sky" disabled={!canAdvance} onClick={next}>
                {step === 'moments' ? 'Show me presets' : 'Next'}
              </Button>
            </>
          )}
        </DialogActions>
      </MountableDialog>

      {explaining && (
        <UseCaseInfoDialog useCase={explaining} onClose={() => setExplaining(undefined)} />
      )}
    </>
  );
};
